import React, {useEffect, useRef, useState, createContext, useMemo} from 'react';
import './App.scss';
import Header from './components/header';
import Footer from './components/footer';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCommentDots} from "@fortawesome/free-solid-svg-icons";
import {useGetSpeciesImages} from "./helpers/useGetSpeciesImages";
import {useBirdData} from "./helpers/useBirdData";
import {usePeriodFilter} from "./helpers/usePeriodFilter";
import {useDataRequest} from "./helpers/useDataRequest";
// import LogRocket from 'logrocket';
import AppRouter from "./AppRouter";
import {AppScreen} from "./app-screen";
import * as localforage from "localforage";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {
    calibrateSightings,
    getChecklistByFamily,
    parseCountabilityBundle,
    type EBirdChecklistByFamily
} from "ebird-mydata-reader";
import {getFamilySlug} from "./helpers/helpers";
import {LIFE_JOURNEY_CACHE_KEY} from "./views/discover/constants";
import {clearDerivedBirdDataCaches} from "./helpers/birdDataCache";
import {useDashboardSummary} from "./helpers/useDashboardSummary";
import FeedbackModal from "./components/feedback";
import {getFeedbackContextFromPath} from "./components/feedback/helpers";
import {usePageTitle} from "./helpers/usePageTitle";
import {registerBirdMapDiagnostics} from "./helpers/cacheDiagnostics";
import {useChecklistReference} from "./helpers/useChecklistReference";
import {buildSpeciesMetadataLookup, SpeciesMetadataEntry} from "./helpers/speciesMetadataLookup";
import {getAlphaStatus, isLocalAlphaFrontendHost} from "./api/alpha";
import AlphaGate from "./components/alpha-gate";
import {
    formatStoredDataRefreshDate,
    getYearsFromStoredBirdData,
    hydrateStoredObservationData,
    STORED_EBIRD_DATA_KEY,
    type StoredBirdDataPayload
} from "./helpers/storedBirdData";
import {
    STORED_COUNTABILITY_CALIBRATION_KEY,
    STORED_COUNTABILITY_CALIBRATION_VERSION
} from "./views/calibrate-data/constants";
import type {StoredCountabilityCalibration} from "./views/calibrate-data/types";

export type AppContextType = {
    lookupByTaxonomicOrder: (taxonomicOrder: number) => SpeciesMetadataEntry|undefined
}

export type SpeciesFamilyLink = {
    familyName: string,
    familyCommonName?: string,
    familySlug: string
}

export type HomeLocation = {
    locationId: string,
    location: string,
    county: string,
    stateProvince: string,
    countryCode: string,
    latitude: number,
    longitude: number
}

export const AppContext = createContext<AppContextType>({
    lookupByTaxonomicOrder: (taxonomicOrder => undefined)
});

const STORED_HOME_LOCATION_KEY = 'storedHomeLocation';
const DEFAULT_MAP_MODE = 'year';
const FAMILY_EXPLORER_ROUTE = '/family-explorer';
const HOTSPOT_EXPLORER_ROUTE = '/hotspot-explorer';
const CALIBRATE_DATA_ROUTE = '/calibrate-data';
type AlphaGateState = 'checking' | 'authorized' | 'required';

const getStoredDataRefreshDate = (storedData: StoredBirdDataPayload | {data?: any[]} | null) => {
    if (!storedData || !('date' in storedData)) return undefined;

    return storedData.date;
};

const getStoredCalibrationDate = (storedCalibration: StoredCountabilityCalibration | null) => {
    if (!storedCalibration || storedCalibration.version !== STORED_COUNTABILITY_CALIBRATION_VERSION) return undefined;

    return storedCalibration.date;
};

function App() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const activeMapSpeciesCode = useRef<string|null>(null);
    const activeMapDate = useRef<string|null>(null);
    const hasHydratedInitialData = useRef(false);
    const [alphaGateState, setAlphaGateState] = useState<AlphaGateState>('checking');
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [rawData, setRawData] = useState<any[]>([]);
    const [years, setYears] = useState<number[]|string[]>([]);
    const [storedDataRefreshedAt, setStoredDataRefreshedAt] = useState<string | undefined>();
    const [storedCalibrationAppliedAt, setStoredCalibrationAppliedAt] = useState<string | undefined>();
    const [rawCalibrationBundle, setRawCalibrationBundle] = useState<string | undefined>();
    const [homeLocation, setHomeLocation] = useState<HomeLocation | undefined>();
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

    const { hasRequestedData, isDataRequestReady, openMyDataRequest, resetMyDataRequest } = useDataRequest();
    const countabilityCalibration = useMemo(() => {
        if (!rawCalibrationBundle) return undefined;

        try {
            return parseCountabilityBundle(rawCalibrationBundle);
        } catch (error) {
            console.error(error);
            return undefined;
        }
    }, [rawCalibrationBundle]);
    const calibrationResult = useMemo(() => {
        if (!rawData.length || !countabilityCalibration) return undefined;

        return calibrateSightings(rawData, countabilityCalibration);
    }, [countabilityCalibration, rawData]);
    const calibrationCacheKey = rawCalibrationBundle
        ? `${storedCalibrationAppliedAt ?? 'calibrated'}:${rawCalibrationBundle.length}`
        : 'uncalibrated';
    const {
        lifeList,
        displayObservationsBySpecies,
        observationsBySpecies,
        observationsByLocation
    } = useBirdData(rawData, calibrationResult, calibrationCacheKey);
    const getScreenForPath = (pathname: string) => {
        if (pathname === '/') return AppScreen.Welcome;
        if (pathname === '/upload') return AppScreen.Upload;
        if (pathname === '/home-location') return AppScreen.HomeLocation;
        if (pathname === '/dashboard') return AppScreen.Dashboard;
        if (pathname === CALIBRATE_DATA_ROUTE) return AppScreen.CalibrateData;
        if (pathname === '/map') return AppScreen.Map;
        if (pathname === '/calendar' || pathname.startsWith('/calendar/')) return AppScreen.Calendar;
        if (pathname === '/wrap-up') return AppScreen.WrapUp;
        if (pathname === '/discover' || pathname === '/discover/life-journey' || pathname === '/discover/birding-personality' || pathname === '/discover/achievements' || pathname.startsWith('/discover/achievements/')) return AppScreen.Discover;
        if (pathname === '/big-year' || pathname === '/discover/big-year') return AppScreen.BigYear;
        if (pathname === FAMILY_EXPLORER_ROUTE || pathname.startsWith(`${FAMILY_EXPLORER_ROUTE}/family/`)) return AppScreen.FamilyExplorer;
        if (pathname === HOTSPOT_EXPLORER_ROUTE || pathname.startsWith(`${HOTSPOT_EXPLORER_ROUTE}/`)) return AppScreen.HotspotExplorer;
        if (pathname === '/checklist' || pathname.startsWith('/checklist/family/')) return AppScreen.FamilyExplorer;

        return AppScreen.Welcome;
    };
    const routeScreen = getScreenForPath(location.pathname);
    const isAlphaGateRequired = alphaGateState === 'required';
    const screen = isInitialLoading || alphaGateState === 'checking' ? AppScreen.Loading : routeScreen;
    const shouldLoadChecklistReference = rawData.length > 0 && [
        AppScreen.Dashboard,
        AppScreen.Map,
        AppScreen.Calendar,
        AppScreen.Discover,
        AppScreen.BigYear,
        AppScreen.FamilyExplorer,
        AppScreen.HotspotExplorer
    ].includes(routeScreen);
    const {checklistReference, isChecklistReferenceLoading} = useChecklistReference(shouldLoadChecklistReference);
    const shouldLoadSpeciesImages = rawData.length > 0 && [
        AppScreen.Dashboard,
        AppScreen.Map,
        AppScreen.Discover,
        AppScreen.BigYear,
        AppScreen.HotspotExplorer
    ].includes(routeScreen);
    const { speciesImages, isSpeciesImagesLoading, getSpeciesImages } = useGetSpeciesImages(shouldLoadSpeciesImages);
    const checklist = useMemo((): EBirdChecklistByFamily[] => {
        if (!observationsBySpecies.length || !checklistReference.length) {
            return [];
        }

        const checklistSeed = JSON.parse(JSON.stringify(checklistReference));

        return getChecklistByFamily(observationsBySpecies, checklistSeed);
    }, [checklistReference, observationsBySpecies]);
    const lookupByTaxonomicOrder = useMemo(() => buildSpeciesMetadataLookup(checklist), [checklist]);
    const {dashboardSummary, isDashboardSummaryLoading} = useDashboardSummary(
        rawData,
        lifeList,
        observationsByLocation,
        checklist,
        years,
        calibrationResult,
        calibrationCacheKey,
        screen === AppScreen.Dashboard
    );
    const {
        year,
        month,
        months,
        filteredObservations,
        setYear,
        setMonth,
        setTimePeriod: setPeriodFilter,
        setSpeciesFilter,
        setDayFilter,
        resetPeriodFilter
    } = usePeriodFilter(rawData, displayObservationsBySpecies, checklist, calibrationResult);

    const hasData = rawData.length > 0;
    const isBirdDataHydrating = hasData && observationsByLocation.length === 0;
    const isChecklistHydrating = shouldLoadChecklistReference && (isChecklistReferenceLoading || checklist.length === 0);
    const isSpeciesImagesHydrating = shouldLoadSpeciesImages && (isSpeciesImagesLoading || Object.keys(speciesImages).length === 0);
    const mapMode = searchParams.get('mode') || DEFAULT_MAP_MODE;
    const mapTour = searchParams.get('tour') === 'true';
    const mapSpeciesCode = searchParams.get('species');
    const mapDate = searchParams.get('date');
    const mapActiveSpecies = mapSpeciesCode
        ? checklist
            .flatMap(family => family.speciesList)
            .find(species => species.speciesCode === mapSpeciesCode)?.taxonomicOrder ?? 0
        : 0;
    const familyByTaxonomicOrder = useMemo(() => {
        const familyLookup = new Map<number, SpeciesFamilyLink>();

        for (const family of checklist) {
            const familyLink = {
                familyName: family.familyName,
                familyCommonName: family.familyCommon,
                familySlug: getFamilySlug(family.familyName)
            };

            for (const species of family.speciesList) {
                familyLookup.set(species.taxonomicOrder, familyLink);
                for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                    familyLookup.set(alternateTaxonomicOrder, familyLink);
                }
            }
        }

        return familyLookup;
    }, [checklist]);
    const parentTaxonomicOrderByTaxonomicOrder = useMemo(() => {
        const parentLookup = new Map<number, number>();

        for (const family of checklist) {
            for (const species of family.speciesList) {
                parentLookup.set(species.taxonomicOrder, species.taxonomicOrder);
                for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                    parentLookup.set(alternateTaxonomicOrder, species.taxonomicOrder);
                }
            }
        }

        return parentLookup;
    }, [checklist]);
    const showHeader = !isAlphaGateRequired && screen !== AppScreen.Loading;
    const showFeedback = !isAlphaGateRequired && screen !== AppScreen.Loading;
    const showNavigation = hasData && ![AppScreen.Welcome, AppScreen.Upload, AppScreen.HomeLocation].includes(screen);
    const isProtectedRoute = ![AppScreen.Loading, AppScreen.Welcome, AppScreen.Upload].includes(routeScreen);
    const getDefaultPageTitle = () => {
        switch (screen) {
            case AppScreen.Loading:
            case AppScreen.Welcome:
                return undefined;
            case AppScreen.Upload:
                return 'Upload Data';
            case AppScreen.HomeLocation:
                return 'Set Home Location';
            case AppScreen.Dashboard:
                return 'Dashboard';
            case AppScreen.CalibrateData:
                return 'Calibrate Data';
            case AppScreen.Map:
                return 'Map';
            case AppScreen.Calendar:
                return 'Calendar';
            case AppScreen.WrapUp:
                return 'Wrap Up';
            case AppScreen.Discover:
                return 'Discover';
            case AppScreen.BigYear:
                return 'Big Year';
            case AppScreen.FamilyExplorer:
                return 'Family Explorer';
            case AppScreen.HotspotExplorer:
                return 'Hotspot Explorer';
            default:
                return undefined;
        }
    };

    usePageTitle(getDefaultPageTitle());

    useEffect(() => {
        let isActive = true;

        async function resolveAlphaGate() {
            try {
                const status = await getAlphaStatus();
                if (!isActive) return;

                setAlphaGateState(status.authorized ? 'authorized' : 'required');
            } catch (error) {
                if (!isActive) return;

                setAlphaGateState(isLocalAlphaFrontendHost() ? 'authorized' : 'required');
            }
        }

        void resolveAlphaGate();

        return () => {
            isActive = false;
        };
    }, []);

    const refreshAlphaStatus = async () => {
        try {
            const status = await getAlphaStatus();
            setAlphaGateState(status.authorized ? 'authorized' : 'required');
        } catch (error) {
            setAlphaGateState(isLocalAlphaFrontendHost() ? 'authorized' : 'required');
        }
    };

    const getDefaultYear = () => {
        const defaultYear = year?.toString() || years[0]?.toString();

        if (defaultYear) {
            setPeriodFilter(defaultYear, 'null');
        }

        return defaultYear;
    }

    const showBigYear = (selectedYear?: string) => {
        const defaultYear = selectedYear || getDefaultYear();
        if (selectedYear) {
            setPeriodFilter(selectedYear, 'null');
        }
        navigate(`/discover/big-year${defaultYear ? `?year=${defaultYear}` : ''}`);
    }

    const openFeedback = () => {
        setIsFeedbackOpen(true);
    }

    const closeFeedback = () => {
        setIsFeedbackOpen(false);
    }

    useEffect(() => {
        let isActive = true;

        async function resolveInitialScreen() {
            if (hasHydratedInitialData.current) {
                setIsInitialLoading(false);
                return;
            }

            const storedData: StoredBirdDataPayload | {data?: any[]} | null = await localforage.getItem(STORED_EBIRD_DATA_KEY);
            const storedHomeLocation: HomeLocation | null = await localforage.getItem(STORED_HOME_LOCATION_KEY);
            const storedCalibration: StoredCountabilityCalibration | null = await localforage.getItem(STORED_COUNTABILITY_CALIBRATION_KEY);
            const storedDataRows = storedData?.data;
            const storedObservations = Array.isArray(storedDataRows)
                ? hydrateStoredObservationData(storedDataRows)
                : [];

            if (!isActive) return;

            if (storedObservations.length) {
                hasHydratedInitialData.current = true;
                setRawData(storedObservations);
                setYears(getYearsFromStoredBirdData(storedObservations));
                setStoredDataRefreshedAt(getStoredDataRefreshDate(storedData));
                setStoredCalibrationAppliedAt(getStoredCalibrationDate(storedCalibration));
                setRawCalibrationBundle(storedCalibration?.rawBundle);
                if (storedHomeLocation) {
                    setHomeLocation(storedHomeLocation);
                }
                if (location.pathname === '/' || location.pathname === '/upload') {
                    navigate(storedHomeLocation ? '/dashboard' : '/home-location', {replace: true});
                }
                setIsInitialLoading(false);
                return;
            }

            hasHydratedInitialData.current = true;
            if (isProtectedRoute) {
                navigate(hasRequestedData ? '/upload' : '/', {replace: true});
            }
            setIsInitialLoading(false);
        }

        if (!isDataRequestReady || alphaGateState !== 'authorized') return;

        void resolveInitialScreen();

        return () => {
            isActive = false;
        }
    }, [alphaGateState, hasRequestedData, isDataRequestReady, isProtectedRoute, location.pathname, navigate]);

    useEffect(() => {
        window.scrollTo({top: 0});
        setIsFeedbackOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!hasData || screen !== AppScreen.Map) {
            activeMapDate.current = null;
            return;
        }

        if (mapDate && /^\d{4}-\d{2}-\d{2}$/.test(mapDate)) {
            if (activeMapDate.current !== mapDate) {
                activeMapDate.current = mapDate;
                activeMapSpeciesCode.current = null;
                setDayFilter(mapDate);
            }
            return;
        }

        activeMapDate.current = null;

        if (mapMode === 'species' && mapSpeciesCode) {
            const selectedSpecies = checklist
                .flatMap(family => family.speciesList)
                .find(species => species.speciesCode === mapSpeciesCode);

            if (selectedSpecies && activeMapSpeciesCode.current !== mapSpeciesCode) {
                activeMapSpeciesCode.current = mapSpeciesCode;
                setYear('life');
                setMonth('null');
                setSpeciesFilter(selectedSpecies.taxonomicOrder);
                return;
            }

            if (selectedSpecies) return;
        }

        activeMapSpeciesCode.current = null;

        const routeYear = searchParams.get('year') || years[0]?.toString();
        const routeMonth = searchParams.get('month') || 'null';

        if (routeYear && (
            year?.toString() !== routeYear ||
            month?.toString() !== routeMonth
        )) {
            setPeriodFilter(routeYear, routeMonth);
        }
    }, [checklist, hasData, mapDate, mapMode, mapSpeciesCode, month, screen, searchParams, setDayFilter, setMonth, setPeriodFilter, setSpeciesFilter, setYear, year, years]);

    useEffect(() => {
        if (!hasData || screen !== AppScreen.BigYear) return;

        const routeYear = searchParams.get('year') || year?.toString() || years[0]?.toString();

        if (routeYear && (year?.toString() !== routeYear || month?.toString() !== 'null')) {
            setPeriodFilter(routeYear, 'null');
        }
    }, [hasData, month, screen, searchParams, setPeriodFilter, year, years]);

    useEffect(() => {
        registerBirdMapDiagnostics({
            rawObservationCount: rawData.length,
            observationsByLocationCount: observationsByLocation.length,
            observationsBySpeciesCount: observationsBySpecies.length,
            checklistFamilyCount: checklist.length
        });
    }, [checklist.length, observationsByLocation.length, observationsBySpecies.length, rawData.length]);

    /*useEffect(() => {
        LogRocket.init('z3y7i6/birdmap-explorer');
    }, []);*/

    const requestDataDownload = () => {
        openMyDataRequest();
        navigate('/upload');
    }

    const showUploadData = () => {
        navigate('/upload');
    }

    const resetDataDownloadRequest = () => {
        resetMyDataRequest();
        navigate('/');
    }

    const showMapForTimePeriod = (year: string, month: string) => {
        setPeriodFilter(year, month);
        navigate(`/map?year=${year}&month=${month}&mode=${DEFAULT_MAP_MODE}`);
    }

    const showTimePeriodSelector = () => {
        navigate('/dashboard');
    }

    const showHomeLocation = () => {
        navigate('/home-location');
    }

    const showTimePeriodSelection = (data: any[], refreshedAt?: string) => {
        setRawData(data);
        setStoredDataRefreshedAt(refreshedAt);
        navigate(homeLocation ? '/dashboard' : '/home-location');
    }

    const applyCalibrationData = async (nextRawCalibrationBundle: string, calibratedAt: string) => {
        parseCountabilityBundle(nextRawCalibrationBundle);
        await clearDerivedBirdDataCaches();
        setRawCalibrationBundle(nextRawCalibrationBundle);
        setStoredCalibrationAppliedAt(calibratedAt);
        navigate('/dashboard');
    }

    const clearCalibrationData = async () => {
        await localforage.removeItem(STORED_COUNTABILITY_CALIBRATION_KEY);
        await clearDerivedBirdDataCaches();
        setRawCalibrationBundle(undefined);
        setStoredCalibrationAppliedAt(undefined);
    }

    const saveHomeLocation = async (location: HomeLocation) => {
        setHomeLocation(location);
        await localforage.setItem(STORED_HOME_LOCATION_KEY, location);
        await localforage.removeItem(LIFE_JOURNEY_CACHE_KEY);
        navigate('/dashboard');
    }

    const skipHomeLocation = async () => {
        setHomeLocation(undefined);
        await localforage.removeItem(STORED_HOME_LOCATION_KEY);
        await localforage.removeItem(LIFE_JOURNEY_CACHE_KEY);
        navigate('/dashboard');
    }

    const resetExplorer = async () => {
        setRawData([]);
        setYears([]);
        setStoredDataRefreshedAt(undefined);
        resetPeriodFilter();
        navigate('/upload');
    }

    const resetStoredBirdData = async () => {
        await localforage.removeItem(STORED_EBIRD_DATA_KEY);
        await clearDerivedBirdDataCaches();
        await resetExplorer();
    }

    const showWrapUp = () => {
        navigate('/wrap-up');
    }

    const showHotspotExplorer = () => {
        navigate(HOTSPOT_EXPLORER_ROUTE);
    }

    return (
        <div className="app-wrapper">
            <AppContext.Provider value={{lookupByTaxonomicOrder}}>
                {showHeader ?
                    <Header
                        hasData={hasData}
                        showNavigation={showNavigation}
                        activeScreen={screen}
                    />
                    : null
                }
                {showFeedback ?
                    <>
                        <FeedbackModal
                            isOpen={isFeedbackOpen}
                            onClose={closeFeedback}
                            pageRoute={`${location.pathname}${location.search}`}
                            pageUrl={window.location.href}
                            {...getFeedbackContextFromPath(location.pathname)}
                        />
                        <button
                            type="button"
                            className="feedback-fab"
                            onClick={openFeedback}
                            aria-label="Open feedback form"
                        >
                            <FontAwesomeIcon icon={faCommentDots} aria-hidden="true" />
                        </button>
                    </>
                    : null
                }
                <main className="app-main">
                    {isAlphaGateRequired ?
                        <AlphaGate onAuthorized={refreshAlphaStatus} />
                        :
                        <AppRouter
                        screen={screen}
                        rawData={rawData}
                        isBirdDataHydrating={isBirdDataHydrating}
                        isChecklistHydrating={isChecklistHydrating}
                        isSpeciesImagesHydrating={isSpeciesImagesHydrating}
                        lifeList={lifeList}
                        years={years}
                        months={months}
                        year={year}
                        month={month}
                        mapMode={mapMode}
                        mapTour={mapTour}
                        mapDate={mapDate ?? undefined}
                        mapActiveSpecies={mapActiveSpecies}
                        filteredObservations={filteredObservations}
                        displayObservationsBySpecies={displayObservationsBySpecies}
                        observationsBySpecies={observationsBySpecies}
                        observationsByLocation={observationsByLocation}
                        homeLocation={homeLocation}
                        checklist={checklist}
                        dashboardSummary={dashboardSummary}
                        isDashboardSummaryLoading={isDashboardSummaryLoading}
                        storedDataRefreshedAt={formatStoredDataRefreshDate(storedDataRefreshedAt)}
                        storedCalibrationAppliedAt={formatStoredDataRefreshDate(storedCalibrationAppliedAt)}
                        familyByTaxonomicOrder={familyByTaxonomicOrder}
                        parentTaxonomicOrderByTaxonomicOrder={parentTaxonomicOrderByTaxonomicOrder}
                        speciesImages={speciesImages}
                        getSpeciesImages={getSpeciesImages}
                        lookupByTaxonomicOrder={lookupByTaxonomicOrder}
                        onRequestData={requestDataDownload}
                        onShowUploadData={showUploadData}
                        onDataLoaded={showTimePeriodSelection}
                        onCalibrationApplied={applyCalibrationData}
                        onCalibrationCleared={clearCalibrationData}
                        onSetYears={setYears}
                        onResetDataRequest={resetDataDownloadRequest}
                        onSetMonth={setMonth}
                        onSetYear={setYear}
                        onSetTimePeriod={showMapForTimePeriod}
                        onSetSpeciesFilter={setSpeciesFilter}
                        onSetHomeLocation={saveHomeLocation}
                        onSkipHomeLocation={skipHomeLocation}
                        onResetStoredData={resetStoredBirdData}
                        onStartBigYear={showBigYear}
                        onShowWrapUp={showWrapUp}
                        onShowTimePeriodSelector={showTimePeriodSelector}
                        onShowHomeLocation={showHomeLocation}
                        onShowHotspotExplorer={showHotspotExplorer}
                    />
                    }
                </main>
                <Footer />
            </AppContext.Provider>
        </div>
    );
}

export default App;
